import { safeParseNullDefault, parseNumberStrict } from 'shouldve-been-js';
import ChecklistType from '../../Enums/ChecklistType';
import { datetimeToApiString } from '../../Utils/DateTimeParser';
import type ChecklistModel from './ChecklistModelModel';
import CategoryChecklistElement from './Elements/CategoryChecklistElementModel';
import CheckboxChecklistElement from './Elements/CheckboxChecklistElementModel';
import type ChecklistElement from './Elements/ChecklistElementModel';
import CountryChecklistElement from './Elements/CountryChecklistElementModel';
import DateChecklistElement from './Elements/DateChecklistElementModel';
import DecimalChecklistElement from './Elements/DecimalChecklistElementModel';
import DepartmentChecklistElement from './Elements/DepartmentChecklistElementModel';
import DropdownChecklistElement from './Elements/DropdownChecklistElementModel';
import InspectingCompanyChecklistElement from './Elements/InspectingCompanyChecklistElementModel';
import InspectorChecklistElement from './Elements/InspectorChecklistElementModel';
import IntegerChecklistElement from './Elements/IntegerChecklistElementModel';
import LookupChecklistElement from './Elements/LookupChecklistElementModel';
import MaskChecklistElement from './Elements/MaskChecklistElementModel';
import SimpleChecklistElement from './Elements/SimpleChecklistElementModel';
import TextAreaChecklistElement from './Elements/TextAreaChecklistElementModel';
import TextFieldChecklistElement from './Elements/TextFieldChecklistElementModel';
import UserChecklistElement from './Elements/UserChecklistElementModel';

class FilledChecklistValueObject {

    private _elementId: string;
    private _value: string | null;
    private _signDate: Date | null;
    private _comment: string | null;

    public constructor(elementId: string, value: string | null, signDate: Date | null, comment: string | null) {

        this._elementId = elementId;
        this._value = value;
        this._signDate = signDate;
        this._comment = comment;
    }

    public get elementId(): string {
        return this._elementId;
    }

    public set elementId(value: string) {
        this._elementId = value;
    }

    public get value(): string | null {
        return this._value;
    }

    public set value(value: string | null) {
        this._value = value;
    }

    public get signDate(): Date | null {
        return this._signDate;
    }

    public set signDate(value: Date | null) {
        this._signDate = value;
    }

    public get comment(): string | null {
        return this._comment;
    }

    public set comment(value: string | null) {
        this._comment = value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
    public static fromIndexedDB(object: any): FilledChecklistValueObject | null {

        const elementId: string | null = safeParseNullDefault<string>(object?._elementId, '');
        const value: string | null = safeParseNullDefault<string>(object?._value, '');
        const signDate: Date | null = safeParseNullDefault<Date>(object?._signDate, new Date());
        const comment: string | null = safeParseNullDefault<string>(object?._comment, '');

        if (elementId === null) {
            return null;
        }

        return new FilledChecklistValueObject(elementId, value, signDate, comment);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-redundant-type-constituents
    public toJson(checklistModel: ChecklistModel): any {

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const jsonObject: any = {
            itemId: this.elementId,
        };

        const checklistElement: ChecklistElement | null = checklistModel.elements.find((element: ChecklistElement) => element.elementId === this.elementId) ?? null;

        if (checklistElement === null) {
            return null;
        }

        if (checklistModel.checklistType === ChecklistType.CHECKLIST) {

            if (checklistElement instanceof SimpleChecklistElement) {
                
                if (checklistElement.hasCheckbox) {
                    jsonObject.intValue = parseNumberStrict(this.value) ?? 0;
                }

                if (checklistElement.hasCheckbox && this.signDate !== null) {
                    jsonObject.signDate = datetimeToApiString(this.signDate, 0);
                }

                if (checklistElement.hasComment && this.comment !== null) {
                    jsonObject.comment = this.comment;
                }

            } else {
                return null;
            }
    
        } else {
    
            if (checklistElement instanceof CheckboxChecklistElement) {
                jsonObject.intValue = parseNumberStrict(this.value) ?? 0;
    
            } else if (checklistElement instanceof TextFieldChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof TextAreaChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof MaskChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof DecimalChecklistElement) {
                jsonObject.floatValue = parseNumberStrict(this.value) ?? 0;
    
            } else if (checklistElement instanceof IntegerChecklistElement) {
                jsonObject.intValue = parseNumberStrict(this.value) ?? 0;
    
            } else if (checklistElement instanceof DateChecklistElement) {
                jsonObject.dateValue = this.value;
    
            } else if (checklistElement instanceof CountryChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof InspectorChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof UserChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof DropdownChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof InspectingCompanyChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof CategoryChecklistElement) {
                jsonObject.intValue = parseNumberStrict(this.value) ?? 0;
    
            } else if (checklistElement instanceof DepartmentChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else if (checklistElement instanceof LookupChecklistElement) {
                jsonObject.blobValue = this.value;
    
            } else {
                return null;
            }
        }

        return jsonObject;
    }
}

export default FilledChecklistValueObject;